<script setup>
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue"
import TreeNode   from "@/Components/Binary/TreeNode.vue"
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from "vue"
import { usePage } from "@inertiajs/vue3"
import axios from "axios"

const props = defineProps({
    tree:         { type: Object, required: true },
    searchUser:   { type: Object, default: null },
    currentDepth: { type: Number, default: 3 },
})

const page           = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')

// DOM refs
const treeContainer = ref(null)
const treeViewport  = ref(null)

// Transform — the viewport is anchored at the container center (top:50% left:50%).
// x/y are offsets from that center — so (0,0) = root card centered. centerTree() just resets to 0,0.
const transform  = reactive({ x: 0, y: 0, scale: 1 })
const isDragging = ref(false)
const dragStart  = reactive({ x: 0, y: 0, tx: 0, ty: 0 })
const pinchDist  = ref(null)

// Tree state
const expandedNodes  = reactive({})
const loadedNodes    = reactive({})
const loadingNodes   = reactive({})
const animatingNodes = reactive({})

// Modal
const modal = reactive({ visible: false, node: null, loading: false })

// SVG connectors (drawn INSIDE viewport — same coordinate space)
const connectors = ref([])

// Merged reactive tree
const rootNode = computed(() => mergeNodeData(props.tree))

function mergeNodeData(node) {
    if (!node) return null
    const override = loadedNodes[node.id]
    const merged   = override ? { ...node, ...override } : { ...node }
    merged._expanded   = expandedNodes[merged.id] !== false
    merged.left_child  = merged._expanded ? mergeNodeData(merged.left_child)  : null
    merged.right_child = merged._expanded ? mergeNodeData(merged.right_child) : null
    return merged
}

const fmt      = v => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(v) || 0)
const fmtN     = v => new Intl.NumberFormat('en-US').format(parseInt(v) || 0)
const initials = n => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?'

// Node click: lazy-load children or toggle
async function handleNodeClick(node) {
    const id = node.id
    if (loadingNodes[id]) return
    if (expandedNodes[id] === true)  { expandedNodes[id] = false; scheduleConnectors(); return }
    if (expandedNodes[id] === false) { expandedNodes[id] = true;  scheduleConnectors(); return }
    if (!node.has_children && !node.left_child && !node.right_child) { openModal(node); return }

    loadingNodes[id] = animatingNodes[id] = true
    try {
        const { data } = await axios.get(`/user/binary/genealogy/children/${id}`)
        if (data.success) {
            loadedNodes[id] = {
                left_child:   data.node.left_child,
                right_child:  data.node.right_child,
                has_children: data.node.has_children,
            }
        }
        expandedNodes[id] = true
    } catch (e) { console.error(e) }
    finally {
        loadingNodes[id] = false
        setTimeout(() => { animatingNodes[id] = false }, 500)
        scheduleConnectors()
    }
}

async function openModal(node) {
    if (!node || typeof node.id === 'undefined' || node.id === null) {
        console.warn('openModal: missing node id', node)
        return
    }

    modal.visible = true; modal.loading = true; modal.node = node
    try {
        const id = Number(node.id)
        const { data } = await axios.get(`/user/binary/genealogy/details/${id}`)
        if (data.success) modal.node = data.node
    } catch (e) { console.error(e) }
    finally { modal.loading = false }
}
const closeModal = () => { modal.visible = false; modal.node = null }

// SVG connectors — measures offsetLeft/offsetTop relative to viewport (no viewport-to-screen conversion needed)
let _raf = null
function scheduleConnectors() {
    if (_raf) cancelAnimationFrame(_raf)
    _raf = requestAnimationFrame(drawConnectors)
}

function offsetsFrom(el, ancestor) {
    let x = 0, y = 0, cur = el
    while (cur && cur !== ancestor) { x += cur.offsetLeft; y += cur.offsetTop; cur = cur.offsetParent }
    return { x, y, w: el.offsetWidth, h: el.offsetHeight }
}

function drawConnectors() {
    if (!treeViewport.value) return
    const vp = treeViewport.value, lines = []
    vp.querySelectorAll('[data-node-id]').forEach(card => {
        const pid = card.dataset.parentId
        if (!pid) return
        const parent = vp.querySelector(`[data-node-id="${pid}"]`)
        if (!parent) return
        const p = offsetsFrom(parent, vp), c = offsetsFrom(card, vp)
        const px = p.x + p.w / 2, py = p.y + p.h
        const cx = c.x + c.w / 2, cy = c.y
        const my = (py + cy) / 2
        lines.push({ id: `${pid}-${card.dataset.nodeId}`, d: `M ${px} ${py} C ${px} ${my}, ${cx} ${my}, ${cx} ${cy}`, pos: card.dataset.position })
    })
    connectors.value = lines
}

// Zoom / Pan
const MIN_SCALE = 0.12, MAX_SCALE = 3

function zoomIn()  { applyZoom(transform.scale + 0.15) }
function zoomOut() { applyZoom(transform.scale - 0.15) }

function applyZoom(raw, ox, oy) {
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, raw))
    if (ox !== undefined) {
        const r = next / transform.scale - 1
        transform.x -= ox * r; transform.y -= oy * r
    }
    transform.scale = next
    scheduleConnectors()
}

function centerTree() {
    transform.scale = 1
    transform.x = 0
    transform.y = 0
    scheduleConnectors()
}

function expandAll(node) {
    if (!node) return
    expandedNodes[node.id] = true
    expandAll(node.left_child); expandAll(node.right_child)
    scheduleConnectors()
}
function collapseAll(node) {
    if (!node) return
    if (node.id !== rootNode.value?.id) expandedNodes[node.id] = false
    collapseAll(node.left_child); collapseAll(node.right_child)
    scheduleConnectors()
}

// Mouse drag
function onMouseDown(e) {
    if (e.button !== 0) return
    isDragging.value = true
    dragStart.x = e.clientX; dragStart.y = e.clientY
    dragStart.tx = transform.x; dragStart.ty = transform.y
    e.preventDefault()
}
function onMouseMove(e) {
    if (!isDragging.value) return
    transform.x = dragStart.tx + (e.clientX - dragStart.x)
    transform.y = dragStart.ty + (e.clientY - dragStart.y)
    scheduleConnectors()
}
function onMouseUp() { isDragging.value = false }

// Wheel zoom toward cursor
function onWheel(e) {
    e.preventDefault()
    if (!treeContainer.value) return
    const r = treeContainer.value.getBoundingClientRect()
    // Cursor offset relative to the viewport anchor (container center + current pan)
    const ox = (e.clientX - r.left - r.width  / 2) - transform.x
    const oy = (e.clientY - r.top  - r.height / 2) - transform.y
    applyZoom(transform.scale + (e.deltaY > 0 ? -0.12 : 0.12), ox, oy)
}

// Touch
function onTouchStart(e) {
    if (e.touches.length === 1) {
        isDragging.value = true
        dragStart.x = e.touches[0].clientX; dragStart.y = e.touches[0].clientY
        dragStart.tx = transform.x; dragStart.ty = transform.y
    } else if (e.touches.length === 2) {
        pinchDist.value = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
    }
}
function onTouchMove(e) {
    e.preventDefault()
    if (e.touches.length === 1 && isDragging.value) {
        transform.x = dragStart.tx + (e.touches[0].clientX - dragStart.x)
        transform.y = dragStart.ty + (e.touches[0].clientY - dragStart.y)
        scheduleConnectors()
    } else if (e.touches.length === 2 && pinchDist.value) {
        const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
        applyZoom(transform.scale * (d / pinchDist.value)); pinchDist.value = d
    }
}
function onTouchEnd() { isDragging.value = false; pinchDist.value = null }

// Lifecycle
const ro = ref(null)
onMounted(() => {
    if (props.tree?.id) expandedNodes[props.tree.id] = true
    nextTick(scheduleConnectors)
    ro.value = new ResizeObserver(scheduleConnectors)
    if (treeViewport.value) ro.value.observe(treeViewport.value)
    window.addEventListener('mouseup',   onMouseUp)
    window.addEventListener('mousemove', onMouseMove)
})
onUnmounted(() => {
    ro.value?.disconnect()
    window.removeEventListener('mouseup',   onMouseUp)
    window.removeEventListener('mousemove', onMouseMove)
})
watch(() => props.tree, () => nextTick(scheduleConnectors), { deep: true })
</script>

<template>
    <UserLayout :page-title="'Binary MLM'" :page-section="'Network Tree'">

        <div class="bgt-page">

            <!-- Header strip -->
            <div class="bgt-welcome bgt-anim-up" style="--d:0ms">
                <div class="bgtw-left">
                    <div class="bgtw-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                        </svg>
                    </div>
                    <div class="bgtw-text">
                        <span class="bgtw-eye">Binary MLM</span>
                        <h1 class="bgtw-title">Network Tree</h1>
                    </div>
                </div>
                <div class="bgtw-legends">
                    <span class="legend-chip bg-green-active">
                        <span class="legend-dot legend-dot--green"></span>Active
                    </span>
                    <span class="legend-chip bg-red-inactive">
                        <span class="legend-dot legend-dot--red"></span>Inactive
                    </span>
                    <span class="legend-chip bg-amber-empty">
                        <span class="legend-dot legend-dot--amber"></span>Empty
                    </span>
                </div>
            </div>

            <!-- Main tree card -->
            <div class="bgt-card bgt-anim-up" style="--d:60ms">

                <!-- Controls bar -->
                <div class="bgt-controls">
                    <!-- Zoom pill -->
                    <div class="bgt-zoom-pill">
                        <button @click="zoomOut" class="ctrl-btn ctrl-btn--edge" title="Zoom Out">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M20 12H4"/>
                            </svg>
                        </button>
                        <span class="bgt-zoom-pct">{{ Math.round(transform.scale * 100) }}%</span>
                        <button @click="zoomIn" class="ctrl-btn ctrl-btn--edge" title="Zoom In">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
                            </svg>
                        </button>
                    </div>

                    <!-- Center -->
                    <button @click="centerTree" class="ctrl-btn" title="Center Tree">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="3" stroke-width="2"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
                        </svg>
                        <span class="bgt-ctrl-label">Center</span>
                    </button>

                    <div class="bgt-sep"></div>

                    <!-- Expand All -->
                    <button @click="expandAll(rootNode)" class="ctrl-btn" title="Expand All">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                        <span class="bgt-ctrl-label">Expand All</span>
                    </button>

                    <!-- Collapse -->
                    <button @click="collapseAll(rootNode)" class="ctrl-btn" title="Collapse All">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                        </svg>
                        <span class="bgt-ctrl-label">Collapse</span>
                    </button>

                    <div class="bgt-hint">
                        <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Pinch to zoom on mobile · Click node to expand or view details
                    </div>
                </div>

                <!-- Canvas -->
                <div class="bgt-canvas-wrap">
                    <div ref="treeContainer" class="tree-canvas"
                         :style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
                         @mousedown="onMouseDown"
                         @wheel.prevent="onWheel"
                         @touchstart.passive="onTouchStart"
                         @touchmove.prevent="onTouchMove"
                         @touchend="onTouchEnd">

                        <!-- Dot-grid background -->
                        <svg class="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <pattern id="treeDots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                                    <circle cx="1" cy="1" r="1" fill="rgba(167,93,237,0.18)"/>
                                </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#treeDots)"/>
                        </svg>

                        <div ref="treeViewport"
                             class="absolute will-change-transform select-none"
                             style="top:50%;left:50%;transform-origin:0 0;"
                             :style="{
                                 transform: `translate(calc(-50% + ${transform.x}px), calc(-50% + ${transform.y}px)) scale(${transform.scale})`,
                                 transition: isDragging ? 'none' : 'transform 0.08s ease-out',
                             }">

                            <!-- SVG connectors -->
                            <svg class="absolute inset-0 pointer-events-none overflow-visible"
                                 style="width:100%;height:100%;z-index:0">
                                <defs>
                                    <filter id="fgl" x="-80%" y="-80%" width="260%" height="260%">
                                        <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b"/>
                                        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                                    </filter>
                                    <filter id="fgr" x="-80%" y="-80%" width="260%" height="260%">
                                        <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b"/>
                                        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                                    </filter>
                                </defs>
                                <path v-for="ln in connectors" :key="ln.id"
                                      :d="ln.d" fill="none" stroke-linecap="round"
                                      :stroke="ln.pos === 'left' ? '#22c55e' : '#a855f7'"
                                      :filter="ln.pos === 'left' ? 'url(#fgl)' : 'url(#fgr)'"
                                      stroke-width="2" opacity="0.75"/>
                            </svg>

                            <!-- Tree content -->
                            <div class="relative z-10 flex justify-center items-start py-12 px-20 min-w-max">
                                <TreeNode
                                    v-if="rootNode"
                                    :node="rootNode"
                                    :is-root="true"
                                    :loading-nodes="loadingNodes"
                                    :animating-nodes="animatingNodes"
                                    :currency-symbol="currencySymbol"
                                    @node-click="handleNodeClick"
                                    @node-info="openModal"
                                    @connectors-update="scheduleConnectors"
                                />
                                <div v-else class="bgt-empty">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                                    </svg>
                                    <p>No binary tree data</p>
                                    <small>Invite members to start building your network.</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div><!-- /bgt-card -->
        </div><!-- /bgt-page -->

        <!-- Detail Modal -->
        <Teleport to="body">
            <Transition name="modal-pop">
                <div v-if="modal.visible"
                     class="fixed inset-0 z-[999] flex items-center justify-center p-4"
                     @click.self="closeModal">
                    <div class="absolute inset-0 bg-black/75 backdrop-blur-md" @click="closeModal"></div>

                    <div class="bgt-modal">

                        <!-- Skeleton -->
                        <div v-if="modal.loading" class="bgt-skel">
                            <div class="bgt-skel-head">
                                <div class="bgt-skel-avatar"></div>
                                <div class="bgt-skel-lines">
                                    <div class="bgt-skel-line bgt-skel-line--w75"></div>
                                    <div class="bgt-skel-line bgt-skel-line--w50"></div>
                                </div>
                            </div>
                            <div class="bgt-skel-grid">
                                <div v-for="i in 3" :key="i" class="bgt-skel-box"></div>
                            </div>
                            <div class="bgt-skel-rows">
                                <div v-for="i in 4" :key="i" class="bgt-skel-line"></div>
                            </div>
                        </div>

                        <template v-else-if="modal.node">
                            <!-- Modal header -->
                            <div class="bgt-modal-hdr">
                                <div class="bgt-modal-deco1"></div>
                                <div class="bgt-modal-deco2"></div>

                                <button @click="closeModal" class="bgt-modal-close">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>

                                <div class="bgt-modal-user">
                                    <div class="bgt-modal-avatar-wrap">
                                        <div class="bgt-modal-avatar">
                                            <img v-if="modal.node.avatar_url"
                                                :src="modal.node.avatar_url"
                                                :alt="modal.node.name"
                                                class="bgt-avatar-img"/>
                                            <div v-else class="bgt-avatar-initials">
                                                {{ initials(modal.node.display_name || modal.node.name) }}
                                            </div>
                                        </div>
                                        <span class="bgt-avatar-status"
                                            :class="modal.node.status === 'active' ? 'bgt-status--active' : 'bgt-status--inactive'">
                                            <svg v-if="modal.node.status === 'active'" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                                            </svg>
                                            <svg v-else fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
                                            </svg>
                                        </span>
                                    </div>
                                    <div class="bgt-modal-info">
                                        <h2 class="bgt-modal-name">{{ modal.node.display_name || modal.node.name }}</h2>
                                        <p class="bgt-modal-email">{{ modal.node.email }}</p>
                                        <div class="bgt-modal-tags">
                                            <span class="bgt-tag bgt-tag--uid">#{{ modal.node.uid }}</span>
                                            <span class="bgt-tag"
                                                :class="modal.node.status === 'active' ? 'bgt-tag--active' : 'bgt-tag--inactive'">
                                                ● {{ modal.node.status === 'active' ? 'Active' : 'Inactive' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Floating stat cards -->
                            <div class="bgt-stat-cards">
                                <div class="modal-stat-card">
                                    <p class="modal-stat-label">Package</p>
                                    <p class="modal-stat-value" style="color:#60a5fa">{{ currencySymbol }}{{ fmt(modal.node.package_amount) }}</p>
                                </div>
                                <div class="modal-stat-card">
                                    <p class="modal-stat-label">L. Team</p>
                                    <p class="modal-stat-value" style="color:#4ade80">{{ fmtN(modal.node.left_team_count) }}</p>
                                </div>
                                <div class="modal-stat-card">
                                    <p class="modal-stat-label">R. Team</p>
                                    <p class="modal-stat-value" style="color:#c084fc">{{ fmtN(modal.node.right_team_count) }}</p>
                                </div>
                            </div>

                            <!-- Detail rows -->
                            <div class="bgt-detail-rows">
                                <div v-for="row in [
                                    {label:'Sponsor',     value: modal.node.sponsor_name || '—'},
                                    {label:'Sponsor UID', value: modal.node.sponsor_uid ? '#'+modal.node.sponsor_uid : '—'},
                                    {label:'Joined',      value: modal.node.joined_at   || '—'},
                                    {label:'Position',    value: modal.node.binary_position || '—'},
                                ]" :key="row.label" class="bgt-detail-row">
                                    <span class="bgt-detail-lbl">{{ row.label }}</span>
                                    <span class="bgt-detail-val">{{ row.value }}</span>
                                </div>

                                <!-- Volume cards -->
                                <div class="bgt-vol-grid">
                                    <div class="bgt-vol-card bgt-vol-card--green">
                                        <p class="bgt-vol-lbl">Left Volume</p>
                                        <p class="bgt-vol-val">{{ currencySymbol }}{{ fmt(modal.node.left_leg_volume) }}</p>
                                    </div>
                                    <div class="bgt-vol-card bgt-vol-card--purple">
                                        <p class="bgt-vol-lbl">Right Volume</p>
                                        <p class="bgt-vol-val">{{ currencySymbol }}{{ fmt(modal.node.right_leg_volume) }}</p>
                                    </div>
                                </div>

                                <!-- Pairing bonus -->
                                <div class="bgt-bonus-bar">
                                    <div>
                                        <p class="bgt-bonus-lbl">Total Pairing Bonus</p>
                                        <p class="bgt-bonus-val">{{ currencySymbol }}{{ fmt(modal.node.total_pairing_bonus) }}</p>
                                    </div>
                                    <div class="bgt-bonus-icon">
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </Transition>
        </Teleport>

    </UserLayout>
</template>

<style>
/* ── page animations ────────────────────────────────────────────── */
@keyframes bgtSlideUp {
    from { opacity:0; transform:translateY(22px) }
    to   { opacity:1; transform:none }
}
.bgt-anim-up {
    animation: bgtSlideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d,0ms);
}

/* ── page ───────────────────────────────────────────────────────── */
.bgt-page {
    padding: 1.5rem 1rem 3rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 1400px;
    margin: 0 auto;
}
@media (min-width:640px)  { .bgt-page { padding: 2rem 1.5rem 3rem; } }
@media (min-width:1024px) { .bgt-page { padding: 2.5rem 2rem 3rem; } }

/* ── welcome strip ──────────────────────────────────────────────── */
.bgt-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:1rem; flex-wrap:wrap;
    background:linear-gradient(135deg,rgba(20,10,40,0.92) 0%,rgba(30,14,60,0.88) 100%);
    border:1px solid rgba(167,93,237,0.22); border-radius:1.25rem;
    padding:1rem 1.25rem; position:relative; overflow:hidden;
}
.bgt-welcome::before {
    content:''; position:absolute; inset:0 0 auto 0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.5),transparent);
}
.bgtw-left { display:flex; align-items:center; gap:.875rem; }
.bgtw-icon {
    width:2.75rem; height:2.75rem; border-radius:.875rem;
    background:rgba(167,93,237,0.18); border:1px solid rgba(167,93,237,0.32);
    display:flex; align-items:center; justify-content:center; flex-shrink:0; color:#a75ded;
}
.bgtw-icon svg { width:1.25rem; height:1.25rem; }
.bgtw-eye   { font-size:.65rem; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:rgba(167,93,237,0.8); display:block; }
.bgtw-title { font-size:1.25rem; font-weight:800; color:#fff; line-height:1.2; margin:0; }
.bgtw-legends { display:flex; align-items:center; gap:.5rem; flex-wrap:wrap; }

/* legend chips */
.legend-chip {
    display:inline-flex; align-items:center; gap:.4rem;
    padding:.32rem .7rem; border-radius:9999px;
    font-size:.7rem; font-weight:700; border:1px solid;
}
.bg-green-active  { background:rgba(34,197,94,0.1);   border-color:rgba(34,197,94,0.3);   color:#4ade80; }
.bg-red-inactive  { background:rgba(239,68,68,0.1);   border-color:rgba(239,68,68,0.3);   color:#f87171; }
.bg-amber-empty   { background:rgba(251,191,36,0.08); border-color:rgba(251,191,36,0.28); color:#fbbf24; }
.legend-dot { width:.5rem; height:.5rem; border-radius:50%; flex-shrink:0; }
.legend-dot--green { background:#22c55e; animation:bgtPulse 1.5s ease-in-out infinite; }
.legend-dot--red   { background:#ef4444; }
.legend-dot--amber { background:#fbbf24; }
@keyframes bgtPulse { 0%,100%{opacity:.5} 50%{opacity:1} }

/* ── main tree card ─────────────────────────────────────────────── */
.bgt-card {
    background:linear-gradient(135deg,rgba(20,10,40,0.92) 0%,rgba(14,6,28,0.95) 100%);
    border:1px solid rgba(167,93,237,0.18); border-radius:1.25rem;
    overflow:hidden; position:relative;
}
.bgt-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.4),transparent);
}

/* controls bar */
.bgt-controls {
    display:flex; align-items:center; gap:.5rem; flex-wrap:wrap;
    padding:.75rem 1rem;
    border-bottom:1px solid rgba(167,93,237,0.12);
    background:rgba(167,93,237,0.04);
}
.bgt-zoom-pill {
    display:flex; align-items:stretch; border-radius:.75rem; overflow:hidden;
    border:1px solid rgba(255,255,255,0.12);
}
.bgt-zoom-pct {
    padding:.35rem .75rem; font-family:monospace; font-size:.75rem; font-weight:700;
    color:rgba(255,255,255,0.85); background:rgba(255,255,255,0.04);
    min-width:3.5rem; text-align:center; display:flex; align-items:center; justify-content:center;
    user-select:none;
}
.bgt-sep  { width:1px; height:1.25rem; background:rgba(255,255,255,0.12); flex-shrink:0; }
.bgt-hint { margin-left:auto; display:none; font-size:.72rem; color:rgba(255,255,255,0.3); display:flex; align-items:center; gap:.375rem; }
@media (min-width:768px) { .bgt-hint { display:flex; } }
.bgt-hint svg { flex-shrink:0; }
.bgt-ctrl-label { display:none; }
@media (min-width:640px) { .bgt-ctrl-label { display:inline; font-size:.72rem; font-weight:600; } }

/* canvas wrap */
.bgt-canvas-wrap { padding:.875rem; }

/* Canvas — always dark (sits inside dark glass card) */
.tree-canvas {
    position: relative;
    height: 74vh;
    min-height: 520px;
    overflow: hidden;
    border-radius: 0.875rem;
    border: 1px solid rgba(167,93,237,0.18);
    background: linear-gradient(135deg, #06021a 0%, #0a0418 45%, #060216 100%);
    box-shadow: inset 0 1px 0 rgba(167,93,237,0.08);
    user-select: none;
}

/* Control button — dark theme */
.ctrl-btn {
    display: inline-flex;
    align-items: center;
    gap: .375rem;
    padding: 0.4rem 0.7rem;
    border-radius: 0.625rem;
    background: rgba(167,93,237,0.08);
    border: 1px solid rgba(167,93,237,0.2);
    color: rgba(255,255,255,0.6);
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
    white-space: nowrap;
}
.ctrl-btn:hover {
    background: rgba(167,93,237,0.2);
    border-color: rgba(167,93,237,0.5);
    color: #c084fc;
    box-shadow: 0 0 0 3px rgba(167,93,237,0.12);
    transform: translateY(-1px);
}
.ctrl-btn:active { transform: scale(0.96) !important; }
.ctrl-btn--edge  { border-radius:0; border:0; background:transparent; }
.ctrl-btn--edge:hover { background:rgba(167,93,237,0.15); border-radius:.5rem; }

/* empty state in canvas */
.bgt-empty {
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    gap:.875rem; padding:8rem 3rem; color:rgba(255,255,255,0.3); text-align:center;
}
.bgt-empty svg { width:5rem; height:5rem; opacity:.3; }
.bgt-empty p   { font-size:1.1rem; font-weight:700; color:rgba(255,255,255,0.4); }
.bgt-empty small { font-size:.8rem; opacity:.7; max-width:18rem; line-height:1.5; }

/* ── Modal ──────────────────────────────────────────────────────── */
.bgt-modal {
    position:relative; z-index:10;
    width:100%; max-width:26rem;
    background:linear-gradient(135deg,rgba(15,7,30,0.98) 0%,rgba(20,10,42,0.98) 100%);
    border:1px solid rgba(167,93,237,0.25); border-radius:1.5rem;
    overflow:hidden; box-shadow:0 25px 80px rgba(0,0,0,0.6);
}

/* skeleton */
.bgt-skel { padding:2rem; display:flex; flex-direction:column; gap:1.25rem; }
.bgt-skel-head { display:flex; align-items:center; gap:1rem; }
.bgt-skel-avatar { width:4rem; height:4rem; border-radius:.875rem; background:rgba(167,93,237,0.15); animation:bgtSkPulse .8s ease-in-out infinite alternate; flex-shrink:0; }
.bgt-skel-lines { flex:1; display:flex; flex-direction:column; gap:.625rem; }
.bgt-skel-line { height:.75rem; border-radius:9999px; background:rgba(167,93,237,0.12); animation:bgtSkPulse .8s ease-in-out infinite alternate; }
.bgt-skel-line--w75 { width:75%; }
.bgt-skel-line--w50 { width:50%; }
.bgt-skel-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:.75rem; }
.bgt-skel-box  { height:4rem; border-radius:.875rem; background:rgba(167,93,237,0.1); animation:bgtSkPulse .8s ease-in-out infinite alternate; }
.bgt-skel-rows { display:flex; flex-direction:column; gap:.625rem; }
@keyframes bgtSkPulse { from{opacity:.4} to{opacity:.9} }

/* modal header */
.bgt-modal-hdr {
    position:relative; padding:2rem 1.5rem 3rem; overflow:hidden;
    background:linear-gradient(135deg,#0f0720 0%,#1a0940 52%,#200d5a 100%);
}
.bgt-modal-deco1 { position:absolute; top:-2rem; right:-2rem; width:9rem; height:9rem; border-radius:50%; background:rgba(167,93,237,0.12); pointer-events:none; }
.bgt-modal-deco2 { position:absolute; bottom:-1rem; left:-1.5rem; width:6rem; height:6rem; border-radius:50%; background:rgba(167,93,237,0.08); pointer-events:none; }
.bgt-modal-close {
    position:absolute; top:1rem; right:1rem; z-index:10;
    width:2rem; height:2rem; border-radius:50%;
    background:rgba(255,255,255,0.1); border:none; cursor:pointer;
    display:flex; align-items:center; justify-content:center;
    color:rgba(255,255,255,0.6); transition:all .2s;
}
.bgt-modal-close:hover { background:rgba(255,255,255,0.2); color:#fff; }
.bgt-modal-close svg { width:1rem; height:1rem; }
.bgt-modal-user { display:flex; align-items:flex-start; gap:1rem; position:relative; z-index:10; }
.bgt-modal-avatar-wrap { position:relative; flex-shrink:0; }
.bgt-modal-avatar {
    width:4.5rem; height:4.5rem; border-radius:.875rem; overflow:hidden;
    border:2px solid rgba(167,93,237,0.4); box-shadow:0 4px 20px rgba(0,0,0,0.4);
}
.bgt-avatar-img { width:100%; height:100%; object-fit:cover; }
.bgt-avatar-initials {
    width:100%; height:100%;
    display:flex; align-items:center; justify-content:center;
    font-size:1.5rem; font-weight:900; color:#fff;
    background:linear-gradient(135deg,#7c3aed,#a75ded);
}
.bgt-avatar-status {
    position:absolute; bottom:-.4rem; right:-.4rem;
    width:1.4rem; height:1.4rem; border-radius:50%;
    border:2px solid rgba(15,7,30,0.98);
    display:flex; align-items:center; justify-content:center;
}
.bgt-avatar-status svg { width:.7rem; height:.7rem; color:#fff; }
.bgt-status--active   { background:#22c55e; }
.bgt-status--inactive { background:#ef4444; }
.bgt-modal-info { color:#fff; min-width:0; padding-top:.2rem; }
.bgt-modal-name  { font-size:1.05rem; font-weight:800; line-height:1.3; margin-bottom:.25rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; padding-right:2rem; }
.bgt-modal-email { font-size:.8rem; color:rgba(167,93,237,0.75); margin-bottom:.5rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.bgt-modal-tags { display:flex; flex-wrap:wrap; gap:.35rem; }
.bgt-tag { font-size:.68rem; font-weight:700; padding:.2rem .55rem; border-radius:9999px; }
.bgt-tag--uid      { background:rgba(255,255,255,0.1); color:rgba(255,255,255,0.7); }
.bgt-tag--active   { background:rgba(34,197,94,0.2); border:1px solid rgba(34,197,94,0.4); color:#4ade80; }
.bgt-tag--inactive { background:rgba(239,68,68,0.2); border:1px solid rgba(239,68,68,0.4); color:#f87171; }

/* stat cards */
.bgt-stat-cards {
    display:grid; grid-template-columns:repeat(3,1fr); gap:.625rem;
    padding:.875rem 1.25rem; margin-top:-1.75rem; position:relative; z-index:10;
}
.modal-stat-card {
    background:rgba(20,10,42,0.95); border-radius:.875rem; padding:.75rem .5rem;
    text-align:center; border:1px solid rgba(167,93,237,0.2);
    box-shadow:0 4px 16px rgba(0,0,0,0.4);
}
.modal-stat-label {
    font-size:.6rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em;
    color:rgba(255,255,255,0.35); margin-bottom:.25rem; line-height:1;
}
.modal-stat-value {
    font-size:.8rem; font-weight:900; line-height:1.3;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}

/* detail rows */
.bgt-detail-rows { padding:.875rem 1.25rem 1.5rem; display:flex; flex-direction:column; gap:.25rem; }
.bgt-detail-row {
    display:flex; align-items:center; justify-content:space-between;
    padding:.625rem .25rem; border-bottom:1px solid rgba(167,93,237,0.08);
}
.bgt-detail-row:last-child { border:0; }
.bgt-detail-lbl { font-size:.68rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,0.3); }
.bgt-detail-val { font-size:.83rem; font-weight:700; color:#fff; }

/* volume cards */
.bgt-vol-grid { display:grid; grid-template-columns:1fr 1fr; gap:.625rem; margin:.5rem 0; }
.bgt-vol-card { border-radius:.875rem; padding:.875rem; border:1px solid; }
.bgt-vol-card--green  { background:rgba(34,197,94,0.06);  border-color:rgba(34,197,94,0.2); }
.bgt-vol-card--purple { background:rgba(168,85,247,0.06); border-color:rgba(168,85,247,0.2); }
.bgt-vol-lbl { font-size:.65rem; font-weight:700; text-transform:uppercase; letter-spacing:.07em; margin-bottom:.25rem; }
.bgt-vol-card--green  .bgt-vol-lbl { color:#4ade80; }
.bgt-vol-card--purple .bgt-vol-lbl { color:#c084fc; }
.bgt-vol-val { font-size:.95rem; font-weight:900; }
.bgt-vol-card--green  .bgt-vol-val { color:#4ade80; }
.bgt-vol-card--purple .bgt-vol-val { color:#c084fc; }

/* pairing bonus bar */
.bgt-bonus-bar {
    display:flex; align-items:center; justify-content:space-between;
    padding:1rem 1.25rem; border-radius:.875rem; margin-top:.5rem;
    background:linear-gradient(135deg,rgba(167,93,237,0.25),rgba(124,58,237,0.2));
    border:1px solid rgba(167,93,237,0.35);
}
.bgt-bonus-lbl { font-size:.7rem; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:rgba(167,93,237,0.8); margin-bottom:.25rem; }
.bgt-bonus-val { font-size:1.5rem; font-weight:900; color:#fff; line-height:1; }
.bgt-bonus-icon {
    width:2.75rem; height:2.75rem; border-radius:.75rem;
    background:rgba(255,255,255,0.12); display:flex; align-items:center; justify-content:center;
    flex-shrink:0; color:#fff;
}
.bgt-bonus-icon svg { width:1.5rem; height:1.5rem; }

/* ── Modal animation ────────────────────────────────────────────── */
.modal-pop-enter-active { transition: opacity 0.2s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1); }
.modal-pop-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.modal-pop-enter-from   { opacity: 0; transform: scale(0.86); }
.modal-pop-leave-to     { opacity: 0; transform: scale(0.94); }
</style>
